import getNested from "../../../../utils/GetNested";

/**
 * Helper valueGetter for survey fields that handles both normal fieldPath access
 * and count summary values stored at the top level of the data object.
 */
const surveyFieldValueGetter = (params: any) => {
	const { data, colDef } = params;
	// First try to get value using fieldPath (normal case)
	const fieldPathValue = getNested(data, colDef.fieldPath, null);
	if (fieldPathValue !== null && fieldPathValue !== undefined && fieldPathValue !== "") {
		return fieldPathValue;
	}

	// If fieldPath doesn't have a value, check for count summary value at data[colId]
	// This handles cases where count summary values are stored at the top level
	if (colDef.field && data && data[colDef.field] !== undefined) {
		return data[colDef.field];
	}

	return "";
};

/**
 * Helper valueGetter for survey person fields that returns an object with _id and name
 * for use with personCellRenderer, and handles count summary values.
 */
const surveyPersonValueGetter = (params: any) => {
	const { data, colDef } = params;

	// First try to get person object using fieldPath
	const person = getNested(data, colDef.fieldPath, null);
	if (person && person._id) {
		return {
			_id: person._id,
			name: person.name || "",
		};
	}

	// If fieldPath doesn't have a value, check for count summary value at data[colId]
	if (colDef.field && data && data[colDef.field] !== undefined) {
		const countValue = data[colDef.field];
		// If it's a number (count summary), return it as-is
		if (typeof countValue === "number" || typeof countValue === "string") {
			return countValue;
		}
		// If it's an object, extract _id and name
		if (countValue && countValue._id) {
			return {
				_id: countValue._id,
				name: countValue.name || "",
			};
		}
	}

	return null;
};

export const ColumnsConfig = {
	reviewFormFields: {
		name: {
			_id: "name",
			name: "Name",
			type: "reviewFormName",
			headerName: "Form Name",
			valueGetter: (params) => {
				return {
					id: params.data.review_form?.id,
					name: params.data.review_form?.name,
				};
			},
		},
		status: {
			_id: "status",
			name: "Status",
			type: "reviewFormStatus",
			headerName: "Form Status",
		},
		authors_due_date: {
			_id: "authors_due_date",
			name: "Author Due Date",
			type: "date",
			headerName: "Authors' Due Date",
		},
		signers_due_date: {
			_id: "signers_due_date",
			name: "Signer Due Date",
			type: "date",
			headerName: "Signers' Due Date",
		},
		completed_date: {
			_id: "completed_date",
			name: "Completed Date",
			type: "date",
			headerName: "Form Complete Date",
		},
		authors: {
			_id: "authors",
			name: "Authors",
			type: "people",
			headerName: "Form Authors",
		},
		signers: {
			_id: "signers",
			name: "Signers",
			type: "people",
			headerName: "Form Signers",
		},
		authoring_completed_at: {
			_id: "authoring_completed_at",
			name: "Authored Date",
			type: "date",
			headerName: "Form Authored Date",
		},
		signing_completed_at: {
			_id: "signing_completed_at",
			name: "Signed Date",
			type: "date",
			headerName: "Form Signed Date",
		},
		author_completion: {
			_id: "author_completion",
			name: "Author Completion",
			type: "percentage",
			headerName: "Author Completion",
		},
		signer_completion: {
			_id: "signer_completion",
			name: "Signer Completion",
			type: "percentage",
			headerName: "Signer Completion",
		},
		form_comment: {
			_id: "form_comment",
			name: "360 Message",
			type: "text",
			headerName: "360 Message",
		},
		participants: {
			_id: "participants",
			name: "Participants",
			type: "people",
			headerName: "Form Participants",
		},
		current_author: {
			_id: "current_author",
			name: "Current Author",
			type: "person",
			headerName: "Current Author",
		},
		current_signer: {
			_id: "current_signer",
			name: "Current Signer",
			type: "person",
			headerName: "Current Signer",
		},
	},
	reviewCycleFields: {
		name: {
			_id: "name",
			name: "Name",
			type: "text",
			headerName: "Cycle Name",
		},
		group: {
			_id: "group",
			name: "Group",
			type: "department",
			headerName: "Cycle Group Name",
		},
		template: {
			_id: "template",
			name: "Template",
			type: "reviewTemplate",
			headerName: "Template Name",
		},
		start_date: {
			_id: "start_date",
			name: "Start Date",
			type: "date",
			headerName: "Cycle Start Date",
		},
		status: {
			_id: "status",
			name: "Status",
			type: "reviewCycleStatus",
			headerName: "Cycle Status",
		},
	},
	reviewFields: {
		subject: {
			_id: "subject",
			name: "Subject",
			type: "person",
			headerName: "Review Subject",
		},
		name: {
			_id: "name",
			name: "Review Name",
			type: "text",
			headerName: "Review Name",
		},
		start_date: {
			_id: "start_date",
			name: "Start Date",
			type: "date",
			headerName: "Review Start Date",
		},
		completed_date: {
			_id: "completed_date",
			name: "Completed Date",
			type: "date",
			headerName: "Review Complete Date",
		},
		participants: {
			_id: "participants",
			name: "Participants",
			type: "people",
			headerName: "Review Participants",
		},
		status: {
			_id: "status",
			name: "Status",
			type: "text",
			headerName: "Review Status",
		},
		easy_add_enabled: {
			_id: "easy_add_enabled",
			name: "360 Enabled",
			type: "boolean",
			headerName: "360 Enabled",
		},
		forms_awaiting_approval: {
			_id: "forms_awaiting_approval",
			name: "360 Forms Awaiting Approval",
			type: "number",
			headerName: "360 Forms Awaiting Approval",
		},
	},
	employeeFields: {
		manager_email: {
			_id: "manager_email",
			name: "Manager's Email",
			type: "text",
			headerName: "Manager's Email",
		},
		employee: {
			_id: "employee",
			name: "Employee",
			type: "person",
			headerName: "Employee",
		},
		has_direct_reports: {
			_id: "has_direct_reports",
			name: "Has Direct Reports",
			type: "boolean",
			headerName: "Has Direct Reports",
		},
	},
	goalFields: {
		assignee: {
			_id: "assignee",
			path: ["assignee"],
			name: "Assignee",
			type: "person",
			headerName: "Assignee",
		},
		name: {
			_id: "name",
			path: ["name"],
			name: "Name",
			type: "goalName",
			headerName: "Name",
		},
		description: {
			_id: "description",
			path: ["description"],
			name: "Description",
			type: "text",
			headerName: "Description",
		},
		status: {
			_id: "status",
			path: ["status"],
			name: "Status",
			type: "goalStatus",
			headerName: "Status",
		},
		last_status_update: {
			_id: "last_goal_status_update",
			path: ["last_goal_status_update"],
			name: "Last Status Update",
			type: "date",
			headerName: "Last Status Update",
		},
		categories: {
			_id: "categories",
			path: ["categories"],
			name: "Categories",
			type: "goalCategories",
			headerName: "Categories",
		},
		start_date: {
			_id: "start_date",
			path: ["start_date"],
			name: "Start Date",
			type: "date",
			headerName: "Start Date",
		},
		due_date: {
			_id: "end_date",
			path: ["end_date"],
			name: "Due Date",
			type: "date",
			headerName: "Due Date",
		},
		outlook: {
			_id: "outlook",
			path: ["outlook"],
			name: "Outlook",
			type: "goalOutlook",
			headerName: "Outlook",
		},
		target: {
			_id: "target",
			path: ["target"],
			name: "Target",
			type: "goalTarget",
			headerName: "Target",
		},
		units: {
			_id: "units",
			path: ["units"],
			name: "Units",
			type: "text",
			headerName: "Units",
		},
		goal_weight: {
			_id: "goal_weight",
			path: ["goal_weight"],
			name: "Goal Weight",
			type: "numeric",
			headerName: "Goal Weight",
		},
		value: {
			_id: "value",
			path: ["value"],
			name: "Current Value",
			type: "numeric",
			headerName: "Current Value",
		},
		current_progress: {
			_id: "current_progress",
			path: ["current_progress"],
			name: "Current Progress",
			type: "goalProgress",
			headerName: "Current Progress",
		},
		transparency: {
			_id: "transparency",
			path: ["transparency"],
			name: "Transparency",
			type: "goalTransparency",
			headerName: "Transparency",
		},
		alignment: {
			_id: "alignment",
			path: ["alignment"],
			name: "Alignment",
			type: "goalAlignment",
			headerName: "Alignment",
		},
		alignment_assignee: {
			_id: "alignment_assignee",
			path: ["alignment", "assignee"],
			name: "Alignment Assignee",
			type: "person",
			headerName: "Alignment Assignee",
		},
		assignee_group: {
			_id: "assignee_group",
			path: ["assignee", "group"],
			name: "Assignee Group",
			type: "department",
			headerName: "Assignee Group",
		},
		creation_date: {
			_id: "creation_date",
			path: ["creation_date"],
			name: "Creation Date",
			type: "date",
			headerName: "Creation Date",
		},
	},
	goalTypeLabels: {
		date: "Date",
		text: "Text",
		person: "Person",
		goalName: "Text",
		numeric: "Numeric",
		department: "Group",
		goalStatus: "Goal Status",
		goalCategories: "Goal Categories",
		goalOutlook: "Goal Outlook",
		goalTarget: "Goal Target",
		goalProgress: "Goal Progress",
		goalTransparency: "Goal Transparency",
		goalAlignment: "Goal",
	},
	surveyTypeLabels: {
		date: "Date",
		text: "Text",
		person: "Person",
		surveyName: "Text",
		numeric: "Numeric",
		department: "Group",
		surveyStatus: "Survey Status",
		surveyQuestions: "Survey Questions",
		percentage: "Percent",
	},
	surveyFields: {
		name: {
			_id: "name",
			path: ["survey_name"],
			name: "SurveyName",
			type: "surveyName",
			headerName: "Survey Name",
			valueGetter: (params) => {
				// First try to get value using fieldPath (normal case)
				const fieldPathValue = getNested(params.data, params.colDef.fieldPath, null);
				if (fieldPathValue !== null && fieldPathValue !== undefined && fieldPathValue !== "") {
					// If we have a value from fieldPath, return the expected structure
					return {
						id: params.data.survey_id || params.data._id,
						name: fieldPathValue,
					};
				}

				// If fieldPath doesn't have a value, check for count summary value at data[colId]
				if (params.colDef.field && params.data && params.data[params.colDef.field] !== undefined) {
					return {
						id: params.data.survey_id || params.data._id,
						name: params.data[params.colDef.field],
					};
				}

				// Fallback to original logic
				return {
					id: params.data.survey_id || params.data._id,
					name: params.data.survey_name,
				};
			},
		},
		status: {
			_id: "status",
			path: ["survey_status"],
			name: "Status",
			type: "surveyStatus",
			headerName: "Survey Status",
			valueGetter: surveyFieldValueGetter,
		},
		creator: {
			_id: "survey_creator",
			path: ["survey_creator"],
			name: "SurveyCreator",
			type: "person",
			headerName: "Survey Creator",
			valueGetter: surveyPersonValueGetter,
		},
		creation_date: {
			_id: "survey_creation_date",
			path: ["survey_creation_date"],
			name: "SurveyCreationDate",
			type: "date",
			headerName: "Survey Creation Date",
			valueGetter: surveyFieldValueGetter,
		},
		published_date: {
			_id: "survey_published_date",
			path: ["survey_published_date"],
			name: "SurveyPublishedDate",
			type: "date",
			headerName: "Survey Published Date",
			valueGetter: surveyFieldValueGetter,
		},
		start_date: {
			_id: "survey_start_date",
			path: ["survey_start_date"],
			name: "SurveyStartDate",
			type: "date",
			headerName: "Survey Start Date",
			valueGetter: surveyFieldValueGetter,
		},
		end_date: {
			_id: "survey_end_date",
			path: ["survey_end_date"],
			name: "SurveyEndDate",
			type: "date",
			headerName: "Survey End Date",
			valueGetter: surveyFieldValueGetter,
		},
		access_count: {
			_id: "survey_access_count",
			path: ["participant_access_count"],
			name: "SurveyAccessCount",
			type: "numeric",
			headerName: "Survey Access Count",
			valueGetter: surveyFieldValueGetter,
		},
		submission_count: {
			_id: "survey_submission_count",
			path: ["participant_submission_count"],
			name: "SurveySubmissionCount",
			type: "numeric",
			headerName: "Survey Submission Count",
			valueGetter: surveyFieldValueGetter,
		},
		questions_answered: {
			_id: "questions_answered",
			path: ["questions_answered_rate"],
			name: "QuestionsAnswered",
			type: "percentage",
			headerName: "Questions Answered",
			valueGetter: surveyFieldValueGetter,
		},
		participant_name: {
			_id: "participant_name",
			path: ["participant"],
			name: "ParticipantName",
			type: "person",
			headerName: "Participant Name",
			valueGetter: surveyPersonValueGetter,
		},
		participant_questions_answered: {
			_id: "participant_questions_answered",
			path: ["participant_questions_answered"],
			name: "ParticipantQuestionsAnswered",
			type: "numeric",
			headerName: "Participant Questions Answered",
			valueGetter: surveyFieldValueGetter,
		},
		participant_submitted_date: {
			_id: "participant_submitted_date",
			path: ["participant_submitted_date"],
			name: "ParticipantSubmittedDate",
			type: "date",
			headerName: "Participant Submitted Date",
			valueGetter: surveyFieldValueGetter,
		},
	},
	surveyQuestionFields: {
		questions: {
			_id: "survey_questions",
			path: ["survey_questions"],
			name: "SurveyQuestions",
			type: "surveyQuestions",
			headerName: "Survey Questions",
		},
	},
};
